import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { StammkundeService } from '../../../Services/stammkunde.service';
import { CaptureQueueService } from '../../../Services/capture-queue.service';
import { TimeEntryStatus, ZeitEintrag, ZeitEintragCreate } from '../../../Models/stammkunde.model';
import { ZeitPerson } from '../zeit-eintrag-form/zeit-eintrag-form.component';

export interface StundenErfassungData {
  auftrag: { id: number; title: string };
  assignmentId: number;
  customerId: number;
  einsatzLabel?: string;
  /** Vorbelegtes Datum (yyyy-MM-dd). */
  defaultDate: string;
  /** Eingeplante Kolonne dieses Einsatzes. */
  plannedPeople: ZeitPerson[];
  /** Vollständige Auswahl: alle Team-Mitglieder + Arbeiter des Stammkunden. */
  rosterPeople: ZeitPerson[];
  useQueue?: boolean;
}

interface StundenRow {
  key: string;
  label: string;
  workerId?: number | null;
  personUserId?: string | null;
  personName?: string | null;
  hours: number | null;
  entryId?: number;
  savedHours?: number;
  status?: TimeEntryStatus;
  planned: boolean;
}

const FREE_KEY = '__free__';

/**
 * Erfasst die Stunden einer ganzen Kolonne für EINEN Einsatz und Tag: der Vorarbeiter
 * sieht die eingeplanten Personen sowie bereits gebuchte, kann Personen aus dem
 * gesamten Team/Arbeiterstamm (oder frei) hinzufügen, Stunden eintragen und wieder
 * entfernen. Neue Buchungen sind offline-fähig; Ändern/Entfernen bestehender braucht
 * Verbindung.
 */
@Component({
  selector: 'app-stunden-erfassung',
  templateUrl: './stunden-erfassung.component.html',
  styleUrls: ['./stunden-erfassung.component.css', '../stammkunden-theme.css']
})
export class StundenErfassungComponent implements OnInit {
  readonly freeKey = FREE_KEY;
  workDate = '';
  rows: StundenRow[] = [];
  addKey = '';
  freeName = '';
  loading = false;
  saving = false;

  constructor(
    public dialogRef: MatDialogRef<StundenErfassungComponent>,
    @Inject(MAT_DIALOG_DATA) public data: StundenErfassungData,
    private stammkundeService: StammkundeService,
    private captureQueue: CaptureQueueService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.workDate = this.data.defaultDate;
    this.resetPlannedRows();
    this.loadExisting();
  }

  get isOffline(): boolean {
    return !this.captureQueue.isOnline;
  }

  /** Roster-Personen, die noch nicht als Zeile vorhanden sind. */
  get availableRoster(): ZeitPerson[] {
    const present = new Set(this.rows.map(r => r.key));
    return (this.data.rosterPeople ?? []).filter(p => !present.has(this.personKey(p)));
  }

  get totalHours(): number {
    return this.rows.reduce((sum, r) => sum + (r.hours || 0), 0);
  }

  // ── Zeilen aufbauen / laden ──

  private resetPlannedRows(): void {
    this.rows = (this.data.plannedPeople ?? []).map(p => this.toRow(p, true));
  }

  private loadExisting(): void {
    if (this.isOffline) { return; } // offline: bereits erfasste Stunden nicht abrufbar
    this.loading = true;
    this.stammkundeService.getTimeEntries(this.data.auftrag.id).subscribe({
      next: entries => {
        this.mergeExisting(entries ?? []);
        this.loading = false;
      },
      error: () => { this.loading = false; }
    });
  }

  private mergeExisting(entries: ZeitEintrag[]): void {
    const mine = entries.filter(e =>
      e.assignmentId === this.data.assignmentId && this.sameDay(e.workDate, this.workDate));
    for (const e of mine) {
      const key = e.workerId != null ? `w:${e.workerId}`
        : e.personUserId ? `u:${e.personUserId}`
          : `n:${e.personName}`;
      let row = this.rows.find(r => r.key === key);
      if (!row) {
        row = {
          key,
          label: e.personName,
          workerId: e.workerId,
          personUserId: e.personUserId,
          personName: e.workerId == null && !e.personUserId ? e.personName : null,
          hours: null,
          planned: false
        };
        this.rows.push(row);
      }
      row.hours = e.hours;
      row.entryId = e.id;
      row.savedHours = e.hours;
      row.status = e.status;
    }
  }

  onDateChange(): void {
    // Datum gewechselt -> Zeilen neu aufbauen und bestehende Buchungen des Tages laden.
    this.resetPlannedRows();
    this.loadExisting();
  }

  // ── Personen hinzufügen / entfernen ──

  addFromRoster(): void {
    if (!this.addKey) { return; }
    if (this.addKey === FREE_KEY) {
      const name = this.freeName.trim();
      if (!name) { return; }
      const key = `n:${name}`;
      if (!this.rows.some(r => r.key === key)) {
        this.rows.push({ key, label: name, personName: name, hours: null, planned: false });
      }
      this.freeName = '';
      this.addKey = '';
      return;
    }
    const person = (this.data.rosterPeople ?? []).find(p => this.personKey(p) === this.addKey);
    if (person && !this.rows.some(r => r.key === this.addKey)) {
      this.rows.push(this.toRow(person, false));
    }
    this.addKey = '';
  }

  async removeRow(row: StundenRow): Promise<void> {
    if (row.entryId != null) {
      if (row.status && row.status !== 'Submitted') {
        this.snackBar.open('Freigegebene/abgerechnete Stunden kann nur der Bauleiter ändern.', 'OK', { duration: 4000 });
        return;
      }
      if (this.isOffline) {
        this.snackBar.open('Zum Entfernen bereits gebuchter Stunden ist Verbindung nötig.', 'OK', { duration: 4000 });
        return;
      }
      try {
        await firstValueFrom(this.stammkundeService.deleteTimeEntry(this.data.auftrag.id, row.entryId));
      } catch (err: any) {
        this.snackBar.open(err?.error?.message || 'Entfernen fehlgeschlagen.', 'OK', { duration: 4000 });
        return;
      }
    }
    this.rows = this.rows.filter(r => r !== row);
  }

  /**
   * Zeile nicht editierbar, wenn bereits freigegeben/abgerechnet (nur Bauleiter) oder
   * offline eine bestehende Buchung (Update braucht Verbindung).
   */
  isRowLocked(row: StundenRow): boolean {
    if (row.status && row.status !== 'Submitted') { return true; }
    return this.isOffline && row.entryId != null;
  }

  setPreset(row: StundenRow, hours: number): void {
    if (this.isRowLocked(row)) { return; }
    row.hours = hours;
  }

  // ── Speichern ──

  async save(): Promise<void> {
    if (this.saving) { return; }
    this.saving = true;

    let created = 0, updated = 0, queued = 0, skipped = 0;
    try {
      for (const row of this.rows) {
        const hours = row.hours || 0;

        if (row.entryId != null) {
          if (this.isRowLocked(row)) { continue; } // freigegeben/abgerechnet oder offline
          // Bestehende Buchung: nur bei geänderter Stundenzahl aktualisieren (online).
          if (hours > 0 && hours !== row.savedHours) {
            if (this.isOffline) { skipped++; continue; }
            await firstValueFrom(this.stammkundeService.updateTimeEntry(
              this.data.auftrag.id, row.entryId, this.buildPayload(row, hours)));
            row.savedHours = hours;
            updated++;
          }
          continue;
        }

        if (hours <= 0) { continue; } // leere Zeile ignorieren

        const payload = this.buildPayload(row, hours);
        if (this.data.useQueue) {
          const label = `${hours} h · ${row.label} · ${this.data.auftrag.title}`;
          const res = await this.captureQueue.captureTimeEntry(
            this.data.auftrag.id, this.data.assignmentId, payload, label);
          if (res.queued) { queued++; } else { created++; }
        } else {
          await firstValueFrom(this.stammkundeService.addTimeEntry(this.data.auftrag.id, payload));
          created++;
        }
      }
    } catch (err: any) {
      this.saving = false;
      this.snackBar.open(err?.error?.message || 'Stunden konnten nicht gespeichert werden.', 'OK', { duration: 5000 });
      return;
    }

    this.dialogRef.close({ ok: true, created, updated, queued, skipped });
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  // ── Helpers ──

  private buildPayload(row: StundenRow, hours: number): ZeitEintragCreate {
    const payload: ZeitEintragCreate = {
      assignmentId: this.data.assignmentId,
      workDate: `${this.workDate}T00:00:00`,
      hours
    };
    if (row.workerId != null) {
      payload.workerId = row.workerId;
    } else if (row.personUserId) {
      payload.personUserId = row.personUserId;
    } else {
      payload.personName = row.personName ?? row.label;
    }
    return payload;
  }

  private toRow(p: ZeitPerson, planned: boolean): StundenRow {
    return {
      key: this.personKey(p),
      label: p.label,
      workerId: p.workerId,
      personUserId: p.personUserId,
      personName: p.workerId == null && !p.personUserId ? p.label : null,
      hours: null,
      planned
    };
  }

  private personKey(p: ZeitPerson): string {
    return p.workerId != null ? `w:${p.workerId}`
      : p.personUserId ? `u:${p.personUserId}`
        : `n:${p.label}`;
  }

  private sameDay(iso: string, ymd: string): boolean {
    const d = new Date(iso);
    const key = `${d.getFullYear()}-${`${d.getMonth() + 1}`.padStart(2, '0')}-${`${d.getDate()}`.padStart(2, '0')}`;
    return key === ymd;
  }
}
